import { ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import { Check2, CheckCircle, ExclamationTriangle, InfoCircle, XCircle } from 'react-bootstrap-icons';
import './StepList.css';

/**
 * Where a step stands. `done` is a step the page saw finish. `started` is a
 * step that sends the person to another page, so this page cannot know it
 * finished: it is marked as visited, never as done.
 */
export type StepStatus = 'todo' | 'current' | 'started' | 'done';

/** A numbered list of the steps of a setup, in the order a person does them. */
export function StepList({ children }: { children: ReactNode }) {
  return <ol className="step-list">{children}</ol>;
}

interface StepProps {
  number: number;
  title: string;
  status: StepStatus;
  /** What the step will do, said before the person does it. */
  description: ReactNode;
  /** The step's controls, and its result once it has run. */
  children?: ReactNode;
}

export function Step({ number, title, status, description, children }: StepProps) {
  return (
    <li
      className={`step-list__step step-list__step--${status}`}
      aria-current={status === 'current' ? 'step' : undefined}
    >
      <span className="step-list__marker" aria-hidden="true">
        {status === 'done' ? <Check2 /> : number}
      </span>
      <div className="step-list__body">
        <div className="step-list__head">
          <h2 className="step-list__title">{title}</h2>
          {status === 'current' && <span className="step-list__tag">Current step</span>}
          {status === 'done' && <span className="step-list__tag step-list__tag--done">Done</span>}
        </div>
        <p className="step-list__desc">{description}</p>
        {children}
      </div>
    </li>
  );
}

const TONE_ICONS = {
  success: <CheckCircle />,
  danger: <XCircle />,
  warning: <ExclamationTriangle />,
  info: <InfoCircle />,
} as const;

interface StepResultProps {
  /** `busy` is work in progress. `muted` is a plain note with no outcome yet. */
  tone: 'success' | 'danger' | 'warning' | 'info' | 'busy' | 'muted';
  /** Drop the space above, for a result that stands on its own with no step over it. */
  flush?: boolean;
  children: ReactNode;
}

/** What a step did, or why it could not: an icon, then the words. */
export function StepResult({ tone, flush = false, children }: StepResultProps) {
  return (
    <div
      className={`step-result step-result--${tone}${flush ? ' step-result--flush' : ''}`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <span className="step-result__icon" aria-hidden="true">
        {tone === 'busy' ? <Spinner size="sm" /> : tone === 'muted' ? <InfoCircle /> : TONE_ICONS[tone]}
      </span>
      <div className="step-result__text">{children}</div>
    </div>
  );
}

export default StepList;
